import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { UUID } from "../../../../utils/uuid";
import type { Tags } from "../../../merge-tag/types";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import type { ToTarget } from "../../../to/use-to-target";
import type { FollowUpChannelWithData, FollowUpSchemaType } from "../../reducer/schema";
import { isChatChannelWithData, isEmailChannelWithData, isSmsChannelWithData } from "../../utils";
import { FollowUpBodySection } from "../follow-up-body/follow-up-body-section";
import { FollowUpMessageChat, type FollowUpMessageChatProps } from "./follow-up-message-chat";
import { FollowUpMessageEmail, type FollowUpMessageEmailProps } from "./follow-up-message-email";
import { FollowUpMessageImport } from "./follow-up-message-import";
import { FollowUpMessageSms } from "./follow-up-message-sms";

interface FollowUpMessageProps {
  original_moment_id: UUID | null;
  moment_id: UUID;
  channel: FollowUpChannelWithData;
  mergeTags: Tags;
  toTarget: ToTarget;
  setDesign: (val: SerialisedLexicalEditorDesign) => void;
  setChannelData: (data: FollowUpChannelWithData) => void;
  design: FollowUpSchemaType["design"];
  restrictedEmailFrom: UUID[];
  errors: FollowUpMessageChatProps["errors"] & FollowUpMessageEmailProps["errors"];
  disabled: boolean;
}

export const FollowUpMessage: FC<FollowUpMessageProps> = ({
  original_moment_id,
  moment_id,
  channel,
  setChannelData,
  mergeTags,
  toTarget,
  setDesign,
  design,
  restrictedEmailFrom,
  errors,
  disabled,
}) => {
  const { t } = useTranslation();

  const [forceRender, setForceRender] = useState(1);

  return (
    <FollowUpBodySection
      title="Message"
      key={forceRender}
      subtitle={t(
        "Modify our template or craft a custom message using our editor. Don’t forget to use merge tags to make your Follow-Up personal.",
      )}
      slots={{
        titleRight: (
          <FollowUpMessageImport
            id={original_moment_id}
            channel={channel.channel}
            setDesign={(val) => {
              setDesign(val);
              setForceRender(forceRender + 1);
            }}
          />
        ),
      }}
    >
      {isChatChannelWithData(channel) && (
        <FollowUpMessageChat
          moment_id={moment_id}
          channel={channel}
          setChannelData={setChannelData}
          mergeTags={mergeTags}
          onChange={setDesign}
          toTarget={toTarget}
          design={design}
          errors={errors}
          disabled={disabled}
        />
      )}

      {isEmailChannelWithData(channel) && (
        <FollowUpMessageEmail
          moment_id={moment_id}
          channel={channel}
          setChannelData={setChannelData}
          mergeTags={mergeTags}
          onChange={setDesign}
          toTarget={toTarget}
          design={design}
          restrictedEmailFrom={restrictedEmailFrom}
          errors={errors}
          disabled={disabled}
        />
      )}

      {isSmsChannelWithData(channel) && (
        <FollowUpMessageSms
          moment_id={moment_id}
          mergeTags={mergeTags}
          onChange={setDesign}
          toTarget={toTarget}
          design={design}
          errors={errors}
          disabled={disabled}
        />
      )}
    </FollowUpBodySection>
  );
};
